import { useCallback, useMemo } from "react"
import { useForm, useWatch } from "react-hook-form"

import { useEmpresaConfiguracionAntifraudePut } from "@finanedi.es/front/common/hooks/api"
import { notifications } from "@finanedi.es/front/common/utils"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  IEmpresaConfiguracionAntifraudePut,
  ILeyAntiFraudeFormValues,
  IUseLeyAntiFraudeFormProps,
} from "../interfaces"
import {
  EUserConfiguracionAntifraudeEstado,
  leyAntiFraudeFormActions,
  leyAntiFraudeFormFields,
  leyAntiFraudeFormSchema,
} from "../utils"

export const useLeyAntiFraudeForm = ({
  onClose,
  nif,
  razonSocial,
  estado,
  motivo,
}: IUseLeyAntiFraudeFormProps) => {
  const { empresaConfiguracionAntifraudePut, loading } =
    useEmpresaConfiguracionAntifraudePut()

  const rhForm = useForm<ILeyAntiFraudeFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(leyAntiFraudeFormSchema) as any,
    mode: "onChange",
    defaultValues: {
      estado: estado ?? null,
      verifactu: estado === EUserConfiguracionAntifraudeEstado.Verifactu,
      exento: estado === EUserConfiguracionAntifraudeEstado.Exento,
      motivo: motivo ?? "",
      file: null,
    },
  })

  const estadoActual = useWatch({
    control: rhForm.control,
    name: "estado",
    defaultValue: estado ?? null,
  })

  const handleCheckboxChange = useCallback(
    (checked: boolean, tipo: EUserConfiguracionAntifraudeEstado) => {
      if (checked) {
        rhForm.setValue("estado", tipo)
        rhForm.setValue(
          "verifactu",
          tipo === EUserConfiguracionAntifraudeEstado.Verifactu
        )
        rhForm.setValue(
          "exento",
          tipo === EUserConfiguracionAntifraudeEstado.Exento
        )

        if (tipo === EUserConfiguracionAntifraudeEstado.Exento) {
          rhForm.setValue("file", null)
        }

        if (tipo === EUserConfiguracionAntifraudeEstado.Verifactu) {
          rhForm.setValue("motivo", "")
        }
      } else {
        rhForm.setValue("estado", null)
        rhForm.setValue("verifactu", false)
        rhForm.setValue("exento", false)
      }
    },
    [rhForm]
  )

  const handleOnSubmit = useCallback(async () => {
    const formValues = rhForm.getValues()

    if (!formValues.estado) return

    const body: IEmpresaConfiguracionAntifraudePut = {
      EstadoConfiguracionAntifraude: formValues.estado,
      Motivo:
        formValues.estado === EUserConfiguracionAntifraudeEstado.Exento
          ? formValues.motivo
          : "",
      ...(formValues.estado === EUserConfiguracionAntifraudeEstado.Verifactu &&
        formValues.file && {
          "DocumentacionVerifactu.FormFile": formValues.file,
        }),
    }

    const res = await empresaConfiguracionAntifraudePut(body)

    if (res) {
      notifications.success({
        body: "Se ha configurado la ley antifraude correctamente",
      })
      onClose()
    }
  }, [rhForm, empresaConfiguracionAntifraudePut, onClose])

  const isFormValid = useMemo(() => {
    return estadoActual !== null
  }, [estadoActual])

  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rhForm: rhForm as any,
    fields: leyAntiFraudeFormFields({
      estado: estadoActual ?? null,
      nif,
      razonSocial,
      handleCheckboxChange,
    }),
    actions: leyAntiFraudeFormActions({
      onClose,
      loading,
      isFormValid,
      errorMessage: rhForm.formState.errors.file
        ? "Complete los datos del/los representante/s, descargue el documento de Verifactu y súbalo firmado digitalmente."
        : "",
    }),
    onSubmit: rhForm.handleSubmit(handleOnSubmit),
    containerStyles: "fs-flex fs-flex-col fs-gap-4 fs-min-h-0 fs-h-full",
    contentStyles: `fs-flex fs-flex-col fs-min-h-0 fs-gap-4 fs-grow fs-w-full fs-overflow-y-auto`,
    actionsStyles: "fs-flex fs-flex-col fs-min-h-0 fs-gap-2 fs-shrink-0",
  }
}
